export const CHECKLIST_STAGES = [
  ['pre_service_inspection', 'Pre-service inspection', 'Pre-inspection'],
  ['job_checklist', 'Job checklist', 'Job checklist'],
  ['final_qc', 'Final QC', 'Final QC'],
]

export const CHECKLIST_STAGE_RESULTS = {
  pre_service_inspection: [
    ['pending', 'Pending'],
    ['ok', 'OK'],
    ['advisory', 'Advisory'],
    ['action_required', 'Action required'],
    ['na', 'N/A'],
  ],
  job_checklist: [
    ['pending', 'Pending'],
    ['completed', 'Completed'],
    ['not_required', 'Not required'],
    ['unable_to_complete', 'Unable to complete'],
    ['additional_work_required', 'Additional work required'],
  ],
  final_qc: [
    ['pending', 'Pending'],
    ['pass', 'Pass'],
    ['fail', 'Fail'],
    ['na', 'N/A'],
  ],
}

const COMMENT_REQUIRED_RESULTS = {
  pre_service_inspection: new Set(['advisory', 'action_required']),
  job_checklist: new Set(['unable_to_complete', 'additional_work_required']),
  final_qc: new Set([]),
}

const DETAIL_RESULTS = {
  pre_service_inspection: new Set(['advisory', 'action_required']),
  job_checklist: new Set(['unable_to_complete', 'additional_work_required']),
  final_qc: new Set(['fail']),
}

const STAGE_QUICK_RESULTS = {
  pre_service_inspection: 'ok',
  job_checklist: 'completed',
  final_qc: 'pass',
}

function text(value) {
  return String(value || '').trim()
}

export function checklistItemForm(item = {}) {
  return {
    result: item.result || 'pending',
    customer_comment: item.customer_comment || '',
    internal_note: item.internal_note || '',
  }
}

export function canAuthoriseChecklistOverride(profile = {}, workshopId = '') {
  const profileWorkshopId = text(profile.workshop_id)
  const targetWorkshopId = text(workshopId)
  return Boolean(
    profileWorkshopId
    && targetWorkshopId
    && profileWorkshopId === targetWorkshopId
    && text(profile.role) === 'owner'
  )
}

export function checklistStageLabel(stage, { short = false } = {}) {
  const row = CHECKLIST_STAGES.find(([key]) => key === stage)
  if (!row) return text(stage).replace(/_/g, ' ')
  return short ? row[2] : row[1]
}

export function allowedChecklistResults(stage) {
  return CHECKLIST_STAGE_RESULTS[stage] || [['pending', 'Pending']]
}

export function isChecklistResultAllowed(stage, result) {
  return allowedChecklistResults(stage).some(([value]) => value === result)
}

export function checklistResultLabel(stage, result) {
  const row = allowedChecklistResults(stage).find(([value]) => value === result)
  return row?.[1] || text(result).replace(/_/g, ' ')
}

export function checklistResultNeedsComment(stage, result) {
  return Boolean(COMMENT_REQUIRED_RESULTS[stage]?.has(result))
}

export function checklistResultNeedsDetails(stage, result) {
  return Boolean(DETAIL_RESULTS[stage]?.has(result))
}

export function checklistStageQuickResult(stage) {
  return STAGE_QUICK_RESULTS[stage] || 'pending'
}

export function mergeChecklistDraftForms(checklists = [], currentForms = {}, dirtyItemIds = []) {
  const dirtyIds = dirtyItemIds instanceof Set
    ? dirtyItemIds
    : new Set((dirtyItemIds || []).map(id => String(id)))

  return checklists.reduce((map, checklist) => {
    const checklistItems = checklist.items || []
    checklistItems.forEach(item => {
      const itemId = String(item.id)
      const existing = currentForms[item.id] || currentForms[itemId]
      map[item.id] = dirtyIds.has(item.id) || dirtyIds.has(itemId)
        ? { ...checklistItemForm(item), ...(existing || {}) }
        : checklistItemForm(item)
    })
    return map
  }, {})
}

export function validateChecklistItemInput(item = {}) {
  const stage = text(item.stage)
  const result = text(item.result || 'pending') || 'pending'
  const customerComment = text(item.customer_comment)
  if (!isChecklistResultAllowed(stage, result)) {
    return {
      valid: false,
      message: `${checklistResultLabel(stage, result)} is not valid for ${checklistStageLabel(stage)}.`,
    }
  }
  if (checklistResultNeedsComment(stage, result) && !customerComment) {
    return {
      valid: false,
      message: `Add a customer-facing comment for ${checklistResultLabel(stage, result).toLowerCase()}.`,
    }
  }
  return { valid: true, message: '' }
}

export function hasCurrentChecklistOverride(item = {}) {
  const override = item.current_override || item.latest_override || null
  if (!override?.created_at || !item.result_updated_at) return Boolean(override?.created_at)
  return new Date(override.created_at).getTime() >= new Date(item.result_updated_at).getTime()
}

export function isBlockingFinalQcItem(item = {}) {
  if (item.stage !== 'final_qc') return false
  if (item.required !== true) return false
  if (item.result === 'pending') return true
  if (item.result === 'fail' && item.failure_blocks_completion !== false) {
    return !hasCurrentChecklistOverride(item)
  }
  return false
}

export function stageProgress(items = [], stage) {
  const rows = items.filter(item => item.stage === stage)
  const total = rows.length
  const complete = rows.filter(item => item.result && item.result !== 'pending').length
  const blockers = rows.filter(isBlockingFinalQcItem)
  return {
    stage,
    label: checklistStageLabel(stage, { short: true }),
    total,
    complete,
    blockers,
    completeLabel: `${complete}/${total}`,
    done: total > 0 && complete === total && blockers.length === 0,
  }
}

export function checklistProgress(checklists = []) {
  const items = checklists.flatMap(checklist => checklist.items || [])
  return CHECKLIST_STAGES.map(([stage]) => stageProgress(items, stage))
}

export function checklistCardViews(checklists = []) {
  return checklists.map(checklist => {
    const items = checklist.items || []
    return {
      id: checklist.id,
      title: text(checklist.snapshot_name) || 'Service Card',
      version: Number(checklist.snapshot_version || 1),
      serviceNames: (checklist.service_links || [])
        .map(link => text(link.service?.name || link.service_name))
        .filter(Boolean),
      stages: CHECKLIST_STAGES.map(([stage, label]) => ({
        stage,
        label,
        items: items.filter(item => item.stage === stage),
        progress: stageProgress(items, stage),
      })),
    }
  })
}

export function readyForCollectionBlockers(checklists = []) {
  return checklists
    .flatMap(checklist => checklist.items || [])
    .filter(isBlockingFinalQcItem)
}

export function normaliseChecklistRows({ checklists = [], items = [], serviceLinks = [], overrides = [] } = {}) {
  const servicesByChecklist = serviceLinks.reduce((map, row) => {
    const key = row.job_checklist_id
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(row)
    return map
  }, new Map())

  const overridesByItem = overrides.reduce((map, row) => {
    const key = row.job_checklist_item_id
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(row)
    return map
  }, new Map())

  const itemsByChecklist = items.reduce((map, row) => {
    const key = row.job_checklist_id
    if (!map.has(key)) map.set(key, [])
    const itemOverrides = (overridesByItem.get(row.id) || [])
      .slice()
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    map.get(key).push({
      ...row,
      overrides: itemOverrides,
      current_override: itemOverrides.find(override => {
        if (!override.created_at) return false
        if (!row.result_updated_at) return true
        return new Date(override.created_at).getTime() >= new Date(row.result_updated_at).getTime()
      }) || null,
    })
    return map
  }, new Map())

  return checklists.map(checklist => ({
    ...checklist,
    service_links: servicesByChecklist.get(checklist.id) || [],
    items: (itemsByChecklist.get(checklist.id) || [])
      .slice()
      .sort((a, b) => {
        const stageSort = CHECKLIST_STAGES.findIndex(([stage]) => stage === a.stage) - CHECKLIST_STAGES.findIndex(([stage]) => stage === b.stage)
        if (stageSort !== 0) return stageSort
        return (Number(a.display_order || 0) - Number(b.display_order || 0)) || text(a.label).localeCompare(text(b.label))
      }),
  }))
}

export function isMissingChecklistSchemaError(error) {
  const message = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].map(text).join(' ').toLowerCase()
  return message.includes('42p01')
    || message.includes('42883')
    || message.includes('schema cache')
    || message.includes('could not find the table')
    || message.includes('could not find the function')
    || (message.includes('relation') && message.includes('does not exist'))
}

export async function ensureGeneralJobChecklists(supabase, jobId) {
  if (!jobId) return { checklists: [], skipped: true }
  const { data, error } = await supabase.rpc('ensure_general_job_checklists', { p_job_id: jobId })
  if (error) {
    if (isMissingChecklistSchemaError(error)) return { checklists: [], skipped: true, missingSchema: true }
    throw error
  }
  return { checklists: data || [], skipped: false }
}

export async function loadGeneralJobChecklists(supabase, { jobId, workshopId }) {
  if (!jobId || !workshopId) return []

  const { data: checklists, error: checklistError } = await supabase
    .from('job_checklists')
    .select('*')
    .eq('workshop_id', workshopId)
    .eq('job_id', jobId)
    .neq('status', 'archived')
    .order('created_at', { ascending: true })
  if (checklistError) {
    if (isMissingChecklistSchemaError(checklistError)) return []
    throw checklistError
  }

  const checklistIds = (checklists || []).map(row => row.id)
  if (!checklistIds.length) return []

  const [itemsResult, servicesResult, overridesResult] = await Promise.all([
    supabase
      .from('job_checklist_items')
      .select('*')
      .eq('workshop_id', workshopId)
      .eq('job_id', jobId)
      .in('job_checklist_id', checklistIds)
      .order('stage', { ascending: true })
      .order('display_order', { ascending: true }),
    supabase
      .from('job_checklist_services')
      .select('*, service:services(id, name)')
      .eq('workshop_id', workshopId)
      .eq('job_id', jobId)
      .in('job_checklist_id', checklistIds),
    supabase
      .from('job_checklist_overrides')
      .select('*')
      .eq('workshop_id', workshopId)
      .eq('job_id', jobId)
      .order('created_at', { ascending: false }),
  ])

  if (itemsResult.error) throw itemsResult.error
  if (servicesResult.error) throw servicesResult.error
  if (overridesResult.error) throw overridesResult.error

  return normaliseChecklistRows({
    checklists: checklists || [],
    items: itemsResult.data || [],
    serviceLinks: servicesResult.data || [],
    overrides: overridesResult.data || [],
  })
}

export async function updateChecklistItem(supabase, item, updates = {}) {
  const payload = {
    result: text(updates.result || item.result || 'pending') || 'pending',
    customer_comment: text(updates.customer_comment),
    internal_note: String(updates.internal_note || ''),
  }
  const validation = validateChecklistItemInput({ ...item, ...payload })
  if (!validation.valid) throw new Error(validation.message)

  const { data, error } = await supabase
    .from('job_checklist_items')
    .update(payload)
    .eq('id', item.id)
    .eq('workshop_id', item.workshop_id)
    .eq('job_id', item.job_id)
    .select('*')
    .single()
  if (error) throw error
  return data
}

export async function createChecklistOverride(supabase, item, reason) {
  const cleanReason = text(reason)
  if (!cleanReason) throw new Error('Add an override reason.')

  const { data: userData } = await supabase.auth.getUser()
  const authorisedBy = userData?.user?.id || null
  const { data, error } = await supabase
    .from('job_checklist_overrides')
    .insert({
      workshop_id: item.workshop_id,
      job_id: item.job_id,
      job_checklist_item_id: item.id,
      reason: cleanReason,
      authorised_by: authorisedBy,
    })
    .select('*')
    .single()
  if (error) throw error
  return data
}
